import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Alert, Button, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon, useSnackbar } from '@mysaiva/core'
import { useApiKeys, useCreateApiKey } from '../../api-keys/useApiKeys'
import { SCOPE_GROUPS } from '../../api-keys/scopeCatalog'
import { getScope } from '../../api-keys/scopeCatalog'
import { DeveloperPlatformEvent, trackDeveloperPlatformEvent } from '../../analytics/events'
import type {
  ApiKey,
  ApiKeySecret,
  ApiKeyStatus,
  CreateApiKeyPayload,
  ScopeGroupId
} from '../../api-keys/apiKeys.types'
import { workspacePath } from '../../navigation'
import { ApiKeysTable } from './ApiKeysTable'
import { CreateApiKeyPanel } from './CreateApiKeyPanel'
import { KeyCreatedPanel } from './KeyCreatedPanel'
import styles from './ApiKeys.module.scss'

export const CREATE_QUERY_PARAM = 'create'

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation()
  return (
    <div className={`${styles.panel} ${styles.empty}`} data-testid="api-keys-empty">
      <span className={styles.emptyIcon} aria-hidden="true">
        <Icon icon="Key" size={28} />
      </span>
      <h2 className={styles.emptyTitle}>{t('developerPlatform.apiKeys.empty.title')}</h2>
      <p className={styles.emptyText}>{t('developerPlatform.apiKeys.empty.text')}</p>
      <Button type="primary" onClick={onCreate} data-testid="api-keys-empty-create">
        {t('developerPlatform.apiKeys.createButton')}
        <Icon icon="ArrowRight" size={16} />
      </Button>
    </div>
  )
}

function LoadingRows() {
  return (
    <div
      className={`${styles.panel} ${styles.skeletonRows}`}
      aria-busy="true"
      data-testid="api-keys-loading"
    >
      <span />
      <span />
      <span />
    </div>
  )
}

/**
 * API Keys list (SAIV-11350): filters, table, empty / loading / error
 * states, and the Create → Key Created panel flow. `?create=1` (used by the
 * dashboard hero CTA) opens the Create panel and is removed from the URL.
 */
export function ApiKeysPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isPending, isError, refetch } = useApiKeys()
  const createMutation = useCreateApiKey()

  const [createOpen, setCreateOpen] = useState(false)
  const [created, setCreated] = useState<ApiKeySecret | null>(null)
  const [scopeFilter, setScopeFilter] = useState<ScopeGroupId | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<ApiKeyStatus | 'all'>('all')

  useEffect(() => {
    if (searchParams.get(CREATE_QUERY_PARAM) === '1') {
      setCreateOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete(CREATE_QUERY_PARAM)
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const filtered = useMemo(() => {
    const keys = data ?? []
    return keys.filter(key => {
      if (statusFilter !== 'all' && key.status !== statusFilter) return false
      if (scopeFilter !== 'all' && !key.scopes.some(id => getScope(id)?.group === scopeFilter))
        return false
      return true
    })
  }, [data, statusFilter, scopeFilter])

  const openCreate = () => {
    createMutation.reset()
    setCreateOpen(true)
  }

  const handleCreate = (payload: CreateApiKeyPayload) => {
    createMutation.mutate(payload, {
      onSuccess: result => {
        setCreateOpen(false)
        setCreated(result)
      }
    })
  }

  const handleCreatedClose = (copied: boolean) => {
    setCreated(null)
    snackbar.success(
      t(
        copied
          ? 'developerPlatform.apiKeys.snackbar.createdCopied'
          : 'developerPlatform.apiKeys.snackbar.created'
      )
    )
  }

  const openDetail = (key: ApiKey) => navigate(`${workspacePath('api-keys')}/${key.id}`)

  let body
  if (isPending) body = <LoadingRows />
  else if (isError)
    body = (
      <Alert
        type="error"
        showIcon
        message={t('developerPlatform.apiKeys.errorTitle')}
        description={t('developerPlatform.apiKeys.errorText')}
        action={
          <Button size="small" onClick={() => refetch()} data-testid="api-keys-retry">
            {t('developerPlatform.apiKeys.actions.retry')}
          </Button>
        }
        data-testid="api-keys-error"
      />
    )
  else if ((data ?? []).length === 0) body = <EmptyState onCreate={openCreate} />
  else body = <ApiKeysTable keys={filtered} onOpen={openDetail} />

  return (
    <section
      className={clsx(styles.page, (createOpen || created !== null) && styles.pageShifted)}
      data-testid="dp-page-api-keys"
    >
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('developerPlatform.pages.apiKeys')}</h1>
          <p className={styles.pageDescription}>{t('developerPlatform.apiKeys.description')}</p>
        </div>
        <div className={styles.headerActions}>
          <Button type="primary" onClick={openCreate} data-testid="api-keys-create">
            {t('developerPlatform.apiKeys.createButton')}
            <Icon icon="ArrowRight" size={16} />
          </Button>
        </div>
      </header>

      <div className={styles.filters}>
        <label className={styles.filter}>
          <span>{t('developerPlatform.apiKeys.filters.scopes')}</span>
          <Select
            value={scopeFilter}
            onChange={value => setScopeFilter(value)}
            options={[
              { value: 'all', label: t('developerPlatform.apiKeys.filters.allScopes') },
              ...SCOPE_GROUPS.map(group => ({ value: group.id, label: t(group.labelKey) }))
            ]}
            data-testid="api-keys-filter-scopes"
          />
        </label>
        <label className={styles.filter}>
          <span>{t('developerPlatform.apiKeys.filters.status')}</span>
          <Select
            value={statusFilter}
            onChange={value => setStatusFilter(value)}
            options={[
              { value: 'all', label: t('developerPlatform.apiKeys.filters.allStatuses') },
              { value: 'active', label: t('developerPlatform.apiKeys.status.active') },
              { value: 'inactive', label: t('developerPlatform.apiKeys.status.inactive') }
            ]}
            data-testid="api-keys-filter-status"
          />
        </label>
      </div>

      {body}

      <CreateApiKeyPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
        error={createMutation.error}
      />
      <KeyCreatedPanel
        open={created !== null}
        result={created}
        onClose={handleCreatedClose}
        onSecretCopied={() =>
          created &&
          trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyCopySecret, {
            key_id: created.key.id,
            environment: created.key.environment
          })
        }
      />
    </section>
  )
}
